import { Fragment, h, FunctionalComponent } from "preact";
import { useCollection } from "./CollectionContext";
import { useState, useEffect } from "preact/hooks";
import ProductCard from "./ProductCard";
import { Product } from "../types";

interface ProductGridProps {
  columns: number;
  query: string;
}

interface PromoBannerProps {
  title: string;
  text: string;
  colorClass: string;
}

interface PromoCardProps {
  title: string;
  button_link: string;
  button_text: string;
  img: string;
  row?: number;
  column?: number;
  position?: string;
  text_color?: string;
  button_color?: string;
}

// PromoBanner component to display promotional banners in the product grid
const PromoBanner: FunctionalComponent<PromoBannerProps> = ({
  title,
  text,
  colorClass,
}) => {
  return (
    <div className={`product-grid__promo-banner ${colorClass}`}>
      <h2
        className="product-grid__promo-banner__heading heading-2"
        dangerouslySetInnerHTML={{ __html: title }}
      ></h2>
      <p className="product-grid__promo-banner__text text-md-body">{text}</p>
    </div>
  );
};

const PromoCard: FunctionalComponent<PromoCardProps> = ({
  title,
  button_link,
  button_text,
  img,
  row,
  column,
  position = "center middle",
  text_color = "white",
  button_color = "black",
}) => {
  // Split position into multiple classes (e.g., "center bottom" becomes two classes)
  const positionClasses = position
    .split(" ")
    .map((pos) => `product-grid__promo-card--${pos.trim()}`)
    .join(" ");

  // Only apply grid positioning if row/column are specified (for the promos array)
  const gridStyle = row !== undefined && column !== undefined 
    ? { "--row": row, "--column": column } 
    : {};

  return (
    <div
      className={`product-grid__promo-card ${positionClasses}`}
      style={gridStyle}
    >
      <img src={img} alt={title} loading="lazy" />
      <div className="product-grid__promo-card__content">
        <h3 className={`product-grid__promo-card__heading heading-3 text--${text_color}`}>{title}</h3>
        <a
          href={button_link}
          className={`product-grid__promo-card__button btn btn-${button_color}`}
        >
          {button_text}
        </a>
      </div>
    </div>
  );
};

// Main ProductGrid component
const ProductGrid: FunctionalComponent<ProductGridProps> = ({
  columns,
  query = "",
}) => {
  const { products, fetchProducts, pagination, selectedFilters, selectedSort } =
    useCollection();
  const [currentPage, setCurrentPage] = useState(
    Number(pagination.currentPage) || 1,
  );
  const [previousPage, setPreviousPage] = useState(
    Number(pagination.currentPage) ? Number(pagination.currentPage) - 1 : 0,
  );
  const template = window.themeVars.store.template;
  let content;

  // Effect to fetch products when filters, sort, or query changes
  useEffect(() => {
    setCurrentPage(Number(pagination.currentPage));
  }, [pagination]);

  // Handle loading more products
  const handleLoadMore = () => {
    const newPage = currentPage + 1;
    fetchProducts(
      selectedFilters,
      {
        ...pagination,
        currentPage: newPage,
      },
      selectedSort,
      true, // Append new products
    );
    setCurrentPage(newPage);
  };

  const handleLoadPrevious = () => {
    fetchProducts(
      selectedFilters,
      {
        ...pagination,
        currentPage: previousPage,
      },
      selectedSort,
      false, // Append new products
      true, // Prepend new products
    );
    setPreviousPage(previousPage - 1);
  };

  // Render products along with promo banners
  const renderProductsWithBanners = (products: Product[]): h.JSX.Element[] => {
    const items: h.JSX.Element[] = [];

    window.themeVars.strings.collection.promos.forEach((promo) => {
      items.push(
        <PromoCard
          key={promo.id}
          title={promo.title}
          button_link={promo.button.url}
          button_text={promo.button.text}
          img={promo.image}
          row={promo.row}
          column={promo.column}
        />,
      );
    });

    const showPromoBannerOne =
      window.themeVars.strings.collection.showPromoBannerOne;
    const collectionHidePromoBannerOne =
      window.themeVars.strings.collection.collectionHidePromoBanner1;

    const showPromoBannerTwo =
      window.themeVars.strings.collection.showPromoBannerTwo;
    const collectionHidePromoBannerTwo =
      window.themeVars.strings.collection.collectionHidePromoBanner2;

    let promoBannerOneTitle = window.themeVars.collection.promo_banner_one.title
      ? window.themeVars.collection.promo_banner_one.title
      : window.themeVars.strings.collection.promoBannerOneTitle;
    const promoBannerOneUrl = window.themeVars.collection.promo_banner_one.url;
    if (promoBannerOneUrl) {
      promoBannerOneTitle = `<a href="${promoBannerOneUrl}">${promoBannerOneTitle}</a>`;
    }
    const promoBannerOneText = window.themeVars.collection.promo_banner_one.text
      ? window.themeVars.collection.promo_banner_one.text
      : window.themeVars.strings.collection.promoBannerOneText;
    const promoBannerOneColor = window.themeVars.collection.promo_banner_one
      .color
      ? window.themeVars.collection.promo_banner_one.color
      : "orange";

    let promoBannerTwoTitle = window.themeVars.collection.promo_banner_two.title
      ? window.themeVars.collection.promo_banner_two.title
      : window.themeVars.strings.collection.promoBannerTwoTitle;
    const promoBannerTwoUrl = window.themeVars.collection.promo_banner_two.url;
    if (promoBannerTwoUrl) {
      promoBannerTwoTitle = `<a href="${promoBannerTwoUrl}">${promoBannerTwoTitle}</a>`;
    }
    const promoBannerTwoText = window.themeVars.collection.promo_banner_two.text
      ? window.themeVars.collection.promo_banner_two.text
      : window.themeVars.strings.collection.promoBannerTwoText;
    const promoBannerTwoColor = window.themeVars.collection.promo_banner_two
      .color
      ? window.themeVars.collection.promo_banner_two.color
      : "blue";

    // Get promo card grid position - convert to number and default to 12
    const promoGridPosition = window.themeVars.strings.collection.promo_grid_position != null 
      ? Number(window.themeVars.strings.collection.promo_grid_position) 
      : 12;

    products.forEach((product, index) => {
      // Insert promo banners at specific positions
      if (!collectionHidePromoBannerOne) {
        if (index === 4 && showPromoBannerOne) {
          items.push(
            <PromoBanner
              key="promo1"
              title={promoBannerOneTitle}
              text={promoBannerOneText}
              colorClass={`product-grid__promo-banner product-grid__promo-banner--${promoBannerOneColor}`}
            />,
          );
        }
      }

      if (!collectionHidePromoBannerTwo) {
        if (index === 8 && showPromoBannerTwo) {
          items.push(
            <PromoBanner
              key="promo2"
              title={promoBannerTwoTitle}
              text={promoBannerTwoText}
              colorClass={`product-grid__promo-banner product-grid__promo-banner--${promoBannerTwoColor}`}
            />,
          );
        }
      }

      // Insert promo card at specified position
      if (
        index === promoGridPosition &&
        window.themeVars.strings.collection.promo_image.includes("no-image") !==
          true
      ) {
        items.push(
          <PromoCard
            key="promoCard"
            title={window.themeVars.strings.collection.promo_title}
            button_link={window.themeVars.strings.collection.promo_button_link}
            button_text={window.themeVars.strings.collection.promo_button_text}
            img={window.themeVars.strings.collection.promo_image}
            position={`${window.themeVars.strings.collection.promo_position_horizontal} ${window.themeVars.strings.collection.promo_position_vertical}`}
            text_color={window.themeVars.strings.collection.promo_text_color}
            button_color={window.themeVars.strings.collection.promo_button_color}
          />,
        );
      }
      items.push(
        <ProductCard key={product.id} product={product} columns={columns} />,
      );
    });

    return items;
  };

  const renderProductGridLoadingState = (): h.JSX.Element[] => {
    return [
      <div
        key="loading-state" // Add a unique key here
        className={`
        container
        product-grid__container
        product-grid__columns--${columns}
      `}
      >
        {Array.from({ length: pagination.perPage }).map((_, index) => (
          <div
            className="
              product-card
              is-loading__skeleton
            "
            key={index}
          ></div>
        ))}
      </div>,
    ];
  };

  if (products === "pending") {
    content = renderProductGridLoadingState();
  }

  // Determine content based on template and product state
  if (template === "search") {
    if (!query) {
      content = (
        <div className="container product-grid__container product-grid__container--pending">
          <p className="text-sm">
            {window.themeVars.strings.search.searchPending}
          </p>
        </div>
      );
    } else if (query && products === "pending") {
      content = renderProductGridLoadingState();
    } else if (query && !products?.length) {
      content = (
        <div className="container product-grid__container product-grid__container--no-results">
          <p className="text-sm">
            {window.themeVars.strings.search.noResultsFound.replace(
              "{{terms}}",
              query,
            )}
          </p>
        </div>
      );
    } else if (Array.isArray(products)) {
      content = (
        <div
          className={`container product-grid__container product-grid__columns--${columns}`}
        >
          {renderProductsWithBanners(products)}
        </div>
      );
    }
  } else if (Array.isArray(products)) {
    const columnsClass = `container product-grid__container product-grid__columns--${columns}`;
    content = (
      <Fragment>
        {currentPage > 1 && previousPage > 0 && (
          <div className="product-grid__load-previous-container">
            <button
              className="product-grid__load-previous btn btn-black"
              onClick={handleLoadPrevious}
            >
              {window.themeVars.strings.collection.loadPrevious}
            </button>
          </div>
        )}
        <div className={columnsClass}>
          {renderProductsWithBanners(products)}
        </div>
        {currentPage < Number(pagination.pages) && (
          <div className="product-grid__load-more-container">
            <button
              className="product-grid__load-more btn btn-black"
              onClick={handleLoadMore}
            >
              {window.themeVars.strings.collection.loadMore}
            </button>
          </div>
        )}
      </Fragment>
    );
  }

  return <section className={`product-grid`}>{content}</section>;
};

export default ProductGrid;
